<script lang="ts">
//
  import { createEventDispatcher } from "svelte";

  export let isOpen = false;
  export let currentName = "";
  export let currentAvatarUrl = "";
  export let loading = false;

  const dispatch = createEventDispatcher();

  let name = "";
  let avatarUrl = "";

  // Reset form when popup opens
  $: if (isOpen) {
    name = currentName;
    avatarUrl = currentAvatarUrl;
    console.log("[DEBUG] SignaturePopup opened with:", { currentName, currentAvatarUrl });
  }

  const close = (): void => {
    if (loading) {
      console.log("[DEBUG] Cannot close popup while loading");
      return; // Prevent closing while loading
    }
    console.log("[DEBUG] Closing signature popup");
    dispatch("close");
  };

  const handleSubmit = (): void => {
    if (loading) {
      console.log("[DEBUG] Already loading, ignoring submit");
      return;
    }

    const trimmedName = name.trim();
    const trimmedAvatarUrl = avatarUrl.trim();

    console.log("[DEBUG] Submitting signature:", {
      name: trimmedName,
      avatar_url: trimmedAvatarUrl
    });

    dispatch("submit", {
      name: trimmedName || "Anonymous",
      avatar_url: trimmedAvatarUrl || "/favicon.png",
    });
  };

  const handleKeydown = (event: KeyboardEvent): void => {
    if (event.key === "Escape" && !loading) {
      close();
    } else if (event.key === "Enter" && (event.ctrlKey || event.metaKey)) {
      event.preventDefault();
      handleSubmit();
    }
  };

  // Watch loading state
  $: {
    console.log("[DEBUG] SignaturePopup loading state:", loading);
  }
</script>

{#if isOpen}
  <!-- svelte-ignore a11y-click-events-have-key-events -->
  <!-- svelte-ignore a11y-no-static-element-interactions -->
  <div class="popup-overlay" on:click={close} on:keydown={handleKeydown}>
    <div class="popup-content" on:click|stopPropagation>
      <header>
        <h2>Edit Profile</h2>
        <button class="close-btn" on:click={close} disabled={loading}>×</button>
      </header>

      <form on:submit|preventDefault={handleSubmit}>
        <div class="form-group">
          <label for="name">Display Name</label>
          <input
            id="name"
            type="text"
            bind:value={name}
            placeholder="Enter your display name"
            disabled={loading}
            required
          />
        </div>

        <div class="form-group">
          <label for="avatar">Avatar URL</label>
          <input
            id="avatar"
            type="text"
            bind:value={avatarUrl}
            placeholder="/favicon.png"
            disabled={loading}
          />
        </div>

        {#if avatarUrl}
          <div class="avatar-preview">
            <span>Preview:</span>
            <img
              src={avatarUrl}
              alt="Avatar preview"
              on:error={(e) => (e.target as HTMLImageElement).src = '/favicon.png'}
            />
          </div>
        {/if}

        <div class="form-actions">
          <button type="button" on:click={close} disabled={loading}>
            Cancel
          </button>
          <button type="submit" disabled={loading || !name.trim()}>
            {loading ? "Saving..." : "Save Profile"}
          </button>
        </div>
      </form>

      {#if loading}
        <div class="loading-overlay">
          <div class="spinner"></div>
        </div>
      {/if}
    </div>
  </div>
{/if}

<style>
.popup-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
}

.popup-content {
  background: white;
  border-radius: 8px;
  width: 100%;
  max-width: 400px;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
}

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
}

.spinner {
  width: 32px;
  height: 32px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid #007bff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  border-bottom: 1px solid #eee;
}

h2 {
  margin: 0;
  font-size: 1.2rem;
}

.close-btn {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
}

.close-btn:hover:not(:disabled) {
  background: #f8f9fa;
}

.close-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

form {
  padding: 1rem;
}

.form-group {
  margin-bottom: 1rem;
}

label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
}

input {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  box-sizing: border-box;
}

input:focus {
  outline: none;
  border-color: #007bff;
}

input:disabled {
  background: #f8f9fa;
  color: #666;
  cursor: not-allowed;
}

.avatar-preview {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 4px;
  margin-bottom: 1rem;
}

.avatar-preview img {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  object-fit: cover;
}

.form-actions {
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
}

button {
  padding: 0.75rem 1rem;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 1rem;
}

button[type="button"] {
  background: #6c757d;
  color: white;
}

button[type="button"]:hover:not(:disabled) {
  background: #5a6268;
}

button[type="submit"] {
  background: #007bff;
  color: white;
}

button[type="submit"]:hover:not(:disabled) {
  background: #0056b3;
}

button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
